'use client';

import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, LabelList,
} from 'recharts';

// Realistic outreach funnel — drop-off at each stage
const funnelData = [
  { stage: 'Leads', count: 247, fill: '#3b82f6' },
  { stage: 'Researched', count: 189, fill: '#6366f1' },
  { stage: 'LinkedIn Req.', count: 134, fill: '#0ea5e9' },
  { stage: 'Connected', count: 83, fill: '#06b6d4' },
  { stage: 'Intro Sent', count: 61, fill: '#8b5cf6' },
  { stage: 'TL Shared', count: 41, fill: '#a855f7' },
  { stage: 'Mtg Requested', count: 28, fill: '#f59e0b' },
  { stage: 'Mtg Confirmed', count: 18, fill: '#10b981' },
  { stage: 'Met at Event', count: 8, fill: '#059669' },
  { stage: 'Follow-up', count: 5, fill: '#047857' },
];

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ value: number; payload: { stage: string; count: number } }>;
}

function CustomTooltip({ active, payload }: CustomTooltipProps) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  const pct = ((d.count / 247) * 100).toFixed(0);
  return (
    <div className="bg-white border border-border rounded-lg shadow-dropdown px-3 py-2.5 text-xs">
      <p className="font-semibold text-foreground mb-1">{d.stage}</p>
      <p className="text-muted-foreground">
        <span className="font-mono font-semibold text-foreground">{d.count}</span> contacts
      </p>
      <p className="text-muted-foreground">
        <span className="font-mono font-semibold text-primary">{pct}%</span> of total leads
      </p>
    </div>
  );
}

export default function OutreachFunnelChart() {
  return (
    <div className="bg-white rounded-xl border border-border shadow-card p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-sm font-semibold text-foreground">Outreach Pipeline Funnel</h2>
          <p className="text-xs text-muted-foreground mt-0.5">NRA Show 2026 · 247 total leads</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <div className="w-2.5 h-2.5 rounded-sm bg-emerald-500" />
            <span>Active stages</span>
          </div>
          <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <div className="w-2.5 h-2.5 rounded-sm bg-blue-400" />
            <span>Outreach stages</span>
          </div>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={funnelData} margin={{ top: 8, right: 8, left: -10, bottom: 0 }} barCategoryGap="20%">
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(214,32%,91%)" vertical={false} />
          <XAxis
            dataKey="stage"
            tick={{ fontSize: 10, fill: 'hsl(215,16%,47%)' }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 10, fill: 'hsl(215,16%,47%)', fontFamily: 'IBM Plex Mono' }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: 'hsl(210,40%,96%)' }} />
          <Bar dataKey="count" radius={[4, 4, 0, 0]}>
            {funnelData.map((entry, index) => (
              <Cell key={`funnel-cell-${index}`} fill={entry.fill} fillOpacity={0.85} />
            ))}
            <LabelList
              dataKey="count"
              position="top"
              style={{ fontSize: 10, fontFamily: 'IBM Plex Mono', fill: 'hsl(222,47%,11%)', fontWeight: 600 }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}